import { useEffect, useRef, useState } from 'react'
import { Check, Plus, Trash2, X } from 'lucide-react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { TASK_STATUS_CATEGORIES } from '../lib/types'

const CATEGORY_META = {
  backlog: { label: 'Backlog', defaultColor: '#94a3b8' },
  not_started: { label: 'Not started', defaultColor: '#94a3b8' },
  active: { label: 'Active', defaultColor: '#a855f7' },
  review: { label: 'Review', defaultColor: '#f59e0b' },
  done: { label: 'Done', defaultColor: '#3b82f6' },
  closed: { label: 'Closed', defaultColor: '#10b981' },
  cancelled: { label: 'Cancelled', defaultColor: '#ef4444' },
}

const STATUS_PALETTE = [
  '#94a3b8',
  '#0EA5E9',
  '#3B82F6',
  '#A855F7',
  '#EC4899',
  '#EF4444',
  '#F97316',
  '#F59E0B',
  '#EAB308',
  '#10B981',
  '#14B8A6',
  '#22D3EE',
]

export function TaskStatusEditor({ open, initial, onClose, onApply }) {
  const [draft, setDraft] = useState(initial)
  const [emptyError, setEmptyError] = useState(null)
  const [triedSubmit, setTriedSubmit] = useState(false)

  useEffect(() => {
    if (open) {
      setDraft(initial.length > 0 ? initial : [])
      setEmptyError(null)
      setTriedSubmit(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function updateAt(idx, patch) {
    setDraft((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)))
  }

  function removeAt(idx) {
    setDraft((prev) => prev.filter((_, i) => i !== idx))
  }

  function addStatus(category) {
    setDraft((prev) => [
      ...prev,
      {
        category,
        name: '',
        color: CATEGORY_META[category].defaultColor,
      },
    ])
  }

  function commit() {
    const cleaned = draft.map((s) => ({ ...s, name: s.name.trim().toUpperCase() }))
    if (cleaned.length === 0) {
      setEmptyError('Add at least one status before applying.')
      return
    }
    const hasBlank = cleaned.some((s) => !s.name)
    if (hasBlank) {
      setDraft(cleaned)
      setTriedSubmit(true)
      setEmptyError(null)
      return
    }
    onApply(cleaned)
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Statuses"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={commit} disabled={draft.length === 0}>
            Apply Changes
          </Button>
        </>
      }
    >
      {emptyError ? (
        <p
          role="alert"
          className="mb-3 px-3 py-2 rounded text-sm text-[color:var(--color-priority-urgent)] bg-[color:var(--color-priority-urgent)]/10"
        >
          {emptyError}
        </p>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {TASK_STATUS_CATEGORIES.map((cat) => {
          const rows = draft
            .map((s, i) => ({ s, i }))
            .filter(({ s }) => s.category === cat)
          return (
            <CategoryColumn
              key={cat}
              label={CATEGORY_META[cat].label}
              onAdd={() => addStatus(cat)}
            >
              {rows.length === 0 ? (
                <button
                  type="button"
                  onClick={() => addStatus(cat)}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-md border border-dashed border-[color:var(--color-rule)] text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:border-[color:var(--color-ink-muted)] cursor-pointer"
                >
                  <Plus size={12} />
                  Add status
                </button>
              ) : null}
              {rows.map(({ s, i }) => (
                <StatusRow
                  key={i}
                  status={s}
                  showBlankError={triedSubmit && !s.name.trim()}
                  onChange={(patch) => updateAt(i, patch)}
                  onRemove={() => removeAt(i)}
                />
              ))}
              {rows.length > 0 ? (
                <button
                  type="button"
                  onClick={() => addStatus(cat)}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-md border border-dashed border-[color:var(--color-rule)] text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:border-[color:var(--color-ink-muted)] cursor-pointer"
                >
                  <Plus size={12} />
                  Add status
                </button>
              ) : null}
            </CategoryColumn>
          )
        })}
      </div>
    </Modal>
  )
}

function CategoryColumn({ label, onAdd, children }) {
  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
          {label}
        </p>
        <button
          type="button"
          onClick={onAdd}
          aria-label={`Add ${label} status`}
          title="Add status"
          className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-canvas)] cursor-pointer"
        >
          <Plus size={12} />
        </button>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  )
}

function StatusRow({ status, showBlankError, onChange, onRemove }) {
  const [paletteOpen, setPaletteOpen] = useState(false)
  const wrapperRef = useRef(null)

  useEffect(() => {
    if (!paletteOpen) return
    function onClick(e) {
      if (!wrapperRef.current?.contains(e.target)) setPaletteOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [paletteOpen])

  return (
    <div>
    <div
      className={
        'flex items-center gap-2 px-2 py-1.5 rounded-md border bg-[color:var(--color-card)] ' +
        (showBlankError
          ? 'border-[color:var(--color-priority-urgent)]'
          : 'border-[color:var(--color-rule)]')
      }
    >
      <div ref={wrapperRef} className="relative shrink-0">
        <button
          type="button"
          onClick={() => setPaletteOpen((v) => !v)}
          aria-label="Change status colour"
          title="Change colour"
          className="h-4 w-4 rounded-full ring-1 ring-inset ring-black/10 cursor-pointer motion-safe:hover:scale-110 transition-transform"
          style={{ backgroundColor: status.color }}
        />
        {paletteOpen ? (
          <div
            className="absolute top-full left-0 mt-2 z-30 w-[12.5rem] p-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-xl"
            role="dialog"
            aria-label="Status colour"
          >
            <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2">
              Colour
            </p>
            <div
              className="grid gap-2"
              style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}
            >
              {STATUS_PALETTE.map((c) => {
                const active = c.toLowerCase() === status.color.toLowerCase()
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      onChange({ color: c })
                      setPaletteOpen(false)
                    }}
                    aria-label={`Pick ${c}`}
                    title={c}
                    className={
                      'relative h-5 w-5 rounded-full transition-transform cursor-pointer motion-safe:hover:scale-110 ' +
                      (active ? 'ring-2 ring-offset-1 ring-[color:var(--color-ink-strong)]' : '')
                    }
                    style={{ backgroundColor: c }}
                  >
                    {active ? (
                      <Check
                        size={10}
                        className="absolute inset-0 m-auto text-white drop-shadow"
                        aria-hidden="true"
                      />
                    ) : null}
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}
      </div>
      <input
        type="text"
        value={status.name}
        onChange={(e) => {
          const next = e.target.value
            .replace(/[^\p{L}\s]/gu, '')
            .toUpperCase()
          onChange({ name: next })
        }}
        onBlur={(e) => {
          const next = e.target.value
            .replace(/[^\p{L}\s]/gu, '')
            .replace(/\s+/g, ' ')
            .trim()
            .toUpperCase()
          onChange({ name: next })
        }}
        maxLength={40}
        style={{ textTransform: 'uppercase' }}
        className="flex-1 min-w-0 bg-transparent text-sm text-[color:var(--color-ink-strong)] outline-none focus:ring-1 focus:ring-[color:var(--color-accent)] rounded px-1"
      />
      <button
        type="button"
        onClick={onRemove}
        aria-label="Delete status"
        title="Delete"
        className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-canvas)] cursor-pointer"
      >
        <Trash2 size={12} />
      </button>
    </div>
    {showBlankError ? (
      <p
        role="alert"
        className="mt-1 px-1 text-xs text-[color:var(--color-priority-urgent)]"
      >
        Status name cannot be blank.
      </p>
    ) : null}
    </div>
  )
}

void X
